/**
 * One of two modules in this repo allowed to touch `localStorage`.
 * The other is `src/lib/theme-store.ts`, which holds one theme name.
 *
 * It stores an **ID token only**. The refresh token must never reach it —
 * Firebase Auth stays on `inMemoryPersistence`, so the SDK creates no store of
 * its own. Why that distinction carries the whole design, and what the
 * `localStorage` choice costs, is in
 * `docs/decisions/0003-ui-internal-persists-one-id-token.md`.
 *
 * super-ui reached the same design first and calls the rule **D8**, after
 * decision 8 of the platform's super-user-origin spec. That decision is scoped
 * to that repo, so this one adopts the rule on its own account rather than
 * citing a governance it is not party to. The rule is identical; only the name
 * and the citation differ, and `tools/oxlint-plugin-web-storage.js` says so
 * where a reader diffing the two configs will look.
 *
 * `.oxlintrc.json` exempts this file for `localStorage` and nothing else.
 * `sessionStorage`, `indexedDB`, `caches`, `document.cookie`, the
 * `window.`-prefixed spellings and the computed form all still fail the gate
 * here, so the direct global below is the only sanctioned way in.
 *
 * Every entry point swallows a storage failure: a browser with storage
 * disabled or a full quota must leave the console working in memory.
 */

/** The only key this module writes. `src/lib/theme-store.ts` writes the other. */
export const SESSION_KEY = 'ui-internal.session'

/** Everything the app keeps between page loads. */
export interface StoredSession {
  /** The ID token itself. Never a refresh token. */
  idToken: string
  /**
   * When the operator was last seen, in milliseconds since the epoch.
   *
   * A timestamp, not a credential. It is stored so a reload cannot hand out a
   * fresh idle allowance, and it is origin-wide so activity in one tab counts
   * for every tab.
   */
  lastActivityAt: number
}

/**
 * Check a parsed value has the shape this module wrote.
 *
 * Anything can be typed into Web Storage by hand, so the shape is checked
 * rather than assumed.
 *
 * @param value - The parsed key contents.
 * @returns The session, or null when the shape is wrong.
 */
function toStoredSession(value: unknown): StoredSession | null {
  if (typeof value !== 'object' || value === null) {
    return null
  }
  if (!('idToken' in value) || !('lastActivityAt' in value)) {
    return null
  }

  const { idToken, lastActivityAt } = value
  if (typeof idToken !== 'string' || idToken === '') {
    return null
  }
  if (typeof lastActivityAt !== 'number' || !Number.isFinite(lastActivityAt)) {
    return null
  }

  return { idToken, lastActivityAt }
}

/**
 * Parse a raw key value into a session.
 *
 * Exported because a `storage` event carries the removed value, and reading
 * what was removed is how a tab tells a sign-out from housekeeping.
 *
 * @param raw - The key's contents.
 * @returns The session, or null when the shape is wrong.
 */
export function parseSession(raw: string): StoredSession | null {
  try {
    return toStoredSession(JSON.parse(raw))
  } catch {
    return null
  }
}

/**
 * Read the stored session.
 *
 * @returns The session, or null when there is none, the key is unreadable, or
 *   its contents are not the shape this module writes.
 */
export function readSession(): StoredSession | null {
  let raw: string | null
  try {
    raw = localStorage.getItem(SESSION_KEY)
  } catch {
    return null
  }

  if (raw === null) {
    return null
  }

  return parseSession(raw)
}

/**
 * Replace the whole key.
 *
 * Private, so no caller can write a partial session and drop the other field.
 *
 * @param session - What to store.
 */
function writeSession(session: StoredSession): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session))
  } catch {
    // Quota, or storage disabled. The session carries on in memory and simply
    // does not survive the next reload.
  }
}

/**
 * Store a freshly minted ID token, keeping the activity stamp.
 *
 * The two writers each read the key back before writing it, and Web Storage
 * offers no compare-and-set, so two tabs writing in the same instant can lose
 * one field of one write. One key cannot fix that. What bounds it is that no
 * reader deletes a token it did not find dead, so the worst case is an older
 * but still valid token on disk until the next refresh writes over it.
 *
 * @param idToken - The token to persist.
 */
export function writeIdToken(idToken: string): void {
  const current = readSession()
  writeSession({
    idToken,
    lastActivityAt: current?.lastActivityAt ?? Date.now()
  })
}

/**
 * Stamp the operator as present, keeping the stored token.
 *
 * A no-op when there is no session. An activity time with no token beside it
 * would be a key outliving the session it belonged to.
 *
 * @param at - The time in milliseconds since the epoch.
 */
export function writeLastActivity(at: number): void {
  const current = readSession()
  if (!current) {
    return
  }

  writeSession({ idToken: current.idToken, lastActivityAt: at })
}

/** Remove the key. The one way a persisted session ends. */
export function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY)
  } catch {
    // Nothing to do: the key is already unreachable.
  }
}
